import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Tooltip,
  Badge,
} from "antd";
import {
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  DollarOutlined,
  UserOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  FileTextOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { ICommissionRequest, CommissionRequestStatus } from "../../types/commissionRequest.type";
import { commissionRequestApi } from "../../api/commissionRequestApi";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";
import dayjs from "dayjs";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

export default function CommissionRequest() {
  const { authState } = useAuth();
  const [commissionRequests, setCommissionRequests] = useState<ICommissionRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    status: undefined as CommissionRequestStatus | undefined,
    salesUserId: undefined as string | undefined,
    search: undefined as string | undefined,
    startDate: undefined as string | undefined,
    endDate: undefined as string | undefined,
  });

  // Модальные окна
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ICommissionRequest | null>(null);

  // Лоадеры
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  // Формы
  const [approveForm] = Form.useForm();
  const [rejectForm] = Form.useForm();

  // Загрузка данных
  const fetchCommissionRequests = async () => {
    setLoading(true);
    try {
      const response = await commissionRequestApi.getCommissionRequests({
        page,
        limit: pageSize,
        ...filters,
      });
      setCommissionRequests(response.requests || []);
      setTotal(response.total || 0);
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при загрузке данных");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommissionRequests();
  }, [page, pageSize, filters]);

  // Просмотр запроса
  const handleViewRequest = (request: ICommissionRequest) => {
    setSelectedRequest(request);
    setIsViewModalOpen(true);
  };

  // Одобрение запроса
  const handleApproveRequest = async (values: any) => {
    if (!selectedRequest) return;
    
    setApproveLoading(true);
    try {
      await commissionRequestApi.approveCommissionRequest(selectedRequest._id, {
        notes: values.notes,
      });
      
      message.success("Запрос одобрен и комиссия начислена");
      setIsApproveModalOpen(false);
      approveForm.resetFields();
      fetchCommissionRequests();
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при одобрении запроса");
    } finally {
      setApproveLoading(false);
    }
  };

  // Отклонение запроса
  const handleRejectRequest = async (values: any) => {
    if (!selectedRequest) return;
    
    setRejectLoading(true);
    try {
      await commissionRequestApi.rejectCommissionRequest({
        id: selectedRequest._id,
        rejectionReason: values.rejectionReason,
        notes: values.notes,
      });
      
      message.success("Запрос отклонен");
      setIsRejectModalOpen(false);
      rejectForm.resetFields();
      fetchCommissionRequests();
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при отклонении запроса");
    } finally {
      setRejectLoading(false);
    }
  };

  // Удаление запроса
  const handleDeleteRequest = async (id: string) => {
    setDeleteLoading(id);
    try {
      await commissionRequestApi.deleteCommissionRequest(id);
      message.success("Запрос удален");
      fetchCommissionRequests();
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при удалении запроса");
    } finally {
      setDeleteLoading(null);
    }
  };

  // Получить цвет статуса
  const getStatusColor = (status: CommissionRequestStatus) => {
    switch (status) {
      case CommissionRequestStatus.PENDING: return "orange";
      case CommissionRequestStatus.APPROVED: return "green";
      case CommissionRequestStatus.REJECTED: return "red";
      default: return "default";
    }
  };

  // Получить текст статуса
  const getStatusText = (status: CommissionRequestStatus) => {
    switch (status) {
      case CommissionRequestStatus.PENDING: return "Ожидает";
      case CommissionRequestStatus.APPROVED: return "Одобрен";
      case CommissionRequestStatus.REJECTED: return "Отклонен";
      default: return "Неизвестно";
    }
  };

  // Колонки таблицы
  const columns = [
    {
      title: "Номер заказа",
      dataIndex: "orderReceipt",
      key: "orderReceipt",
      render: (receipt: string) => (
        <Space>
          <ShoppingOutlined />
          <Text strong>{receipt}</Text>
        </Space>
      ),
    },
    {
      title: "Продажник",
      dataIndex: "salesUserName",
      key: "salesUserName",
      render: (name: string) => (
        <Space>
          <UserOutlined />
          <Text>{name}</Text>
        </Space>
      ),
    },
    {
      title: "Клиент",
      dataIndex: "clientName",
      key: "clientName",
    },
    {
      title: "Товар",
      dataIndex: "itemName",
      key: "itemName",
      render: (name: string, record: ICommissionRequest) => (
        <Space direction="vertical" size="small">
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {record.quantity} {record.unitOfMeasurement}
          </Text>
        </Space>
      ),
    },
    {
      title: "Наценка",
      key: "margin",
      render: (record: ICommissionRequest) => (
        <Space direction="vertical" size="small">
          <Text type="secondary" style={{ fontSize: "12px" }}>
            Цена: {record.salePrice.toLocaleString()} {record.currency}
          </Text>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            Мин: {record.minPrice.toLocaleString()} {record.currency}
          </Text>
          <Text strong style={{ color: "#52c41a" }}>
            Наценка: {record.marginPerUnit.toLocaleString()} {record.currency}/ед
          </Text>
        </Space>
      ),
    },
    {
      title: "Комиссия",
      key: "commission",
      render: (record: ICommissionRequest) => (
        <Space direction="vertical" size="small">
          <Text strong style={{ color: "#1890ff", fontSize: "16px" }}>
            {record.commissionInTJS.toLocaleString()} TJS
          </Text>
          {record.tariffRulePercent && (
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {record.tariffRuleName} ({record.tariffRulePercent}%)
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      render: (status: CommissionRequestStatus) => (
        <Badge 
          status={getStatusColor(status) as any} 
          text={getStatusText(status)}
        />
      ),
    },
    {
      title: "Дата создания",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => (
        <Space>
          <CalendarOutlined />
          <Text>{dayjs(date).format("DD.MM.YYYY HH:mm")}</Text>
        </Space>
      ),
    },
    {
      title: "Действия",
      key: "actions",
      render: (record: ICommissionRequest) => (
        <Space>
          <Tooltip title="Просмотр">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewRequest(record)}
            />
          </Tooltip>
          
          {record.status === CommissionRequestStatus.PENDING && 
           (authState.user?.role === UserRole.Admin) && (
            <>
              <Tooltip title="Одобрить">
                <Button
                  type="text"
                  icon={<CheckOutlined />}
                  style={{ color: "green" }}
                  loading={approveLoading}
                  onClick={() => {
                    setSelectedRequest(record);
                    setIsApproveModalOpen(true);
                  }}
                />
              </Tooltip>
              <Tooltip title="Отклонить">
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  style={{ color: "red" }}
                  loading={rejectLoading}
                  onClick={() => {
                    setSelectedRequest(record);
                    setIsRejectModalOpen(true);
                  }}
                />
              </Tooltip>
              <Popconfirm
                title="Удалить запрос?"
                description="Это действие нельзя отменить"
                onConfirm={() => handleDeleteRequest(record._id)}
                okText="Да"
                cancelText="Нет"
                disabled={deleteLoading === record._id}
              >
                <Tooltip title="Удалить">
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    style={{ color: "red" }}
                    loading={deleteLoading === record._id}
                  />
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  // Фильтры
  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates && dates.length === 2) {
      setFilters(prev => ({
        ...prev,
        startDate: dates[0].format("DD.MM.YYYY"),
        endDate: dates[1].format("DD.MM.YYYY"),
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        startDate: undefined,
        endDate: undefined,
      }));
    }
    setPage(1);
  };

  return (
    <div style={{ padding: "24px" }}>
      <Card>
        <Title level={2}>Запросы на комиссию</Title>
        
        {/* Фильтры */}
        <Card style={{ marginBottom: "16px" }}>
          <Row gutter={16}>
            <Col span={6}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text strong>Статус</Text>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Все статусы"
                  allowClear
                  value={filters.status}
                  onChange={(value) => handleFilterChange("status", value)}
                >
                  <Option value={CommissionRequestStatus.PENDING}>Ожидает</Option>
                  <Option value={CommissionRequestStatus.APPROVED}>Одобрен</Option>
                  <Option value={CommissionRequestStatus.REJECTED}>Отклонен</Option>
                </Select>
              </Space>
            </Col>
            <Col span={6}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text strong>Поиск</Text>
                <Input
                  placeholder="Номер заказа, клиент, товар..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  allowClear
                />
              </Space>
            </Col>
            <Col span={12}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text strong>Период</Text>
                <RangePicker
                  style={{ width: "100%" }}
                  format="DD.MM.YYYY"
                  onChange={handleDateRangeChange}
                />
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Таблица */}
        <Table
          columns={columns}
          dataSource={commissionRequests}
          loading={loading}
          rowKey="_id"
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showTotal: (total) => `Всего: ${total}`,
            onChange: (page, pageSize) => {
              setPage(page);
              setPageSize(pageSize);
            },
          }}
        />
      </Card>

      {/* Модальное окно просмотра */}
      <Modal
        title="Детали запроса на комиссию"
        open={isViewModalOpen}
        onCancel={() => {
          setIsViewModalOpen(false);
          setSelectedRequest(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setIsViewModalOpen(false);
            setSelectedRequest(null);
          }}>
            Закрыть
          </Button>,
        ]}
        width={800}
      >
        {selectedRequest && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Номер заказа" span={2}>
              <Text strong>{selectedRequest.orderReceipt}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Продажник">
              <Space>
                <UserOutlined />
                <Text>{selectedRequest.salesUserName}</Text>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Клиент">
              <Text>{selectedRequest.clientName}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Товар" span={2}>
              <Space direction="vertical" size="small">
                <Text strong>{selectedRequest.itemName}</Text>
                <Text type="secondary">
                  {selectedRequest.quantity} {selectedRequest.unitOfMeasurement}
                </Text>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Цена продажи">
              <Text strong>{selectedRequest.salePrice.toLocaleString()} {selectedRequest.currency}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Минимальная цена">
              <Text>{selectedRequest.minPrice.toLocaleString()} {selectedRequest.currency}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Наценка за единицу">
              <Text strong style={{ color: "#52c41a" }}>
                {selectedRequest.marginPerUnit.toLocaleString()} {selectedRequest.currency}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Общая наценка">
              <Text strong style={{ color: "#52c41a" }}>
                {selectedRequest.totalMargin.toLocaleString()} {selectedRequest.currency}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Комиссия (TJS)" span={2}>
              <Text strong style={{ color: "#1890ff", fontSize: "18px" }}>
                {selectedRequest.commissionInTJS.toLocaleString()} TJS
              </Text>
            </Descriptions.Item>
            {selectedRequest.tariffRuleName && (
              <Descriptions.Item label="Правило тарифа" span={2}>
                <Space>
                  <Text>{selectedRequest.tariffRuleName}</Text>
                  {selectedRequest.tariffRulePercent && (
                    <Tag color="blue">{selectedRequest.tariffRulePercent}%</Tag>
                  )}
                </Space>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Статус" span={2}>
              <Badge 
                status={getStatusColor(selectedRequest.status) as any} 
                text={getStatusText(selectedRequest.status)}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Дата создания" span={2}>
              <Space>
                <CalendarOutlined />
                <Text>{dayjs(selectedRequest.createdAt).format("DD.MM.YYYY HH:mm")}</Text>
              </Space>
            </Descriptions.Item>
            {selectedRequest.approvedAt && (
              <>
                <Descriptions.Item label="Одобрен">
                  <Space>
                    <CheckCircleFilled style={{ color: "#52c41a" }} />
                    <Text>{dayjs(selectedRequest.approvedAt).format("DD.MM.YYYY HH:mm")}</Text>
                  </Space>
                </Descriptions.Item>
                {selectedRequest.approvalNotes && (
                  <Descriptions.Item label="Примечание при одобрении" span={2}>
                    <Text>{selectedRequest.approvalNotes}</Text>
                  </Descriptions.Item>
                )}
              </>
            )}
            {selectedRequest.rejectedAt && (
              <>
                <Descriptions.Item label="Отклонен">
                  <Space>
                    <CloseCircleFilled style={{ color: "#ff4d4f" }} />
                    <Text>{dayjs(selectedRequest.rejectedAt).format("DD.MM.YYYY HH:mm")}</Text>
                  </Space>
                </Descriptions.Item>
                {selectedRequest.rejectionReason && (
                  <Descriptions.Item label="Причина отклонения" span={2}>
                    <Text type="danger">{selectedRequest.rejectionReason}</Text>
                  </Descriptions.Item>
                )}
              </>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* Модальное окно одобрения */}
      <Modal
        title="Одобрить запрос на комиссию"
        open={isApproveModalOpen}
        onCancel={() => {
          setIsApproveModalOpen(false);
          approveForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={approveForm}
          onFinish={handleApproveRequest}
          layout="vertical"
        >
          {selectedRequest && (
            <Space direction="vertical" style={{ width: "100%", marginBottom: "16px" }}>
              <Text strong>Комиссия будет начислена:</Text>
              <Text style={{ fontSize: "18px", color: "#1890ff" }}>
                {selectedRequest.commissionInTJS.toLocaleString()} TJS
              </Text>
            </Space>
          )}
          <Form.Item
            name="notes"
            label="Примечание (необязательно)"
          >
            <TextArea rows={4} placeholder="Добавьте примечание к одобрению..." />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<CheckOutlined />}
                loading={approveLoading}
              >
                Одобрить
              </Button>
              <Button onClick={() => {
                setIsApproveModalOpen(false);
                approveForm.resetFields();
              }}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно отклонения */}
      <Modal
        title="Отклонить запрос на комиссию"
        open={isRejectModalOpen}
        onCancel={() => {
          setIsRejectModalOpen(false);
          rejectForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={rejectForm}
          onFinish={handleRejectRequest}
          layout="vertical"
        >
          <Form.Item
            name="rejectionReason"
            label="Причина отклонения"
            rules={[{ required: true, message: "Укажите причину отклонения" }]}
          >
            <TextArea rows={4} placeholder="Укажите причину отклонения запроса..." />
          </Form.Item>
          <Form.Item
            name="notes"
            label="Примечание (необязательно)"
          >
            <TextArea rows={3} placeholder="Добавьте примечание..." />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                danger
                htmlType="submit"
                icon={<CloseOutlined />}
                loading={rejectLoading}
              >
                Отклонить
              </Button>
              <Button onClick={() => {
                setIsRejectModalOpen(false);
                rejectForm.resetFields();
              }}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

